import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Loader2, AlertCircle, ArrowRight, Sparkles, ShieldCheck, Zap } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/Logo";
import { AmbientMesh } from "@/components/layout/AmbientMesh";
import { useAuth } from "@/hooks/useAuth";
import { formatApiErrorDetail } from "@/services/api";

const HIGHLIGHTS = [
  { icon: Zap, title: "One operating system", body: "Orders, inbound, inventory and returns in a single command center." },
  { icon: ShieldCheck, title: "Multi-tenant by design", body: "Every warehouse is isolated with strict role-based access — audited on every move." },
  { icon: Sparkles, title: "Built to feel fast", body: "Realtime status, keyboard-first flows and refined micro-interactions everywhere." },
];

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || "/dashboard";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen overflow-hidden bg-background text-foreground" data-testid="login-page">
      <AmbientMesh />

      {/* Left — form */}
      <div className="relative z-[1] flex w-full items-center justify-center px-6 py-12 sm:px-12 lg:w-[52%] lg:px-20">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
          className="w-full max-w-sm"
        >
          <Link to="/" className="inline-flex" data-testid="login-home-link">
            <Logo className="mb-10" />
          </Link>

          <div className="mb-8">
            <p className="overline">Sign in</p>
            <h1 className="font-display mt-2 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Welcome back.
            </h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Enter your credentials to jump back into your operations.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-start gap-2 rounded-xl border border-rose-500/25 bg-rose-500/10 px-3 py-2.5 text-sm text-rose-700 dark:text-rose-300"
                data-testid="login-error"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-11 bg-background/60 backdrop-blur-sm"
                data-testid="login-email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  className="text-sm font-medium text-primary transition-colors hover:text-primary/80"
                  data-testid="forgot-password-link"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 bg-background/60 pr-10 backdrop-blur-sm"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition-colors hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="group h-11 w-full gap-2 text-sm font-semibold shadow-[0_10px_30px_-10px_hsl(var(--primary)/0.6)]"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
                </>
              ) : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-10 text-xs text-muted-foreground">
            © 2026 FulfillFlow. All rights reserved. ·{" "}
            <Link to="/" className="underline-offset-4 hover:text-foreground hover:underline">Back to site</Link>
          </p>
        </motion.div>
      </div>

      {/* Right — glass showcase panel */}
      <div className="relative z-[1] hidden lg:block lg:w-[48%]">
        <div className="relative flex h-full flex-col justify-between p-14">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.5 }}
          >
            <span className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/50 px-3 py-1 text-xs font-medium text-muted-foreground backdrop-blur-md">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_10px_hsl(160_84%_45%)]" />
              Live · v1.0
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="glass-strong shadow-premium rounded-3xl p-10"
          >
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-foreground">
              The operating system for modern fulfillment.
            </h2>
            <p className="mt-3 text-sm text-muted-foreground">
              Precision logistics, unbound scale — a multi-tenant command center for every warehouse you run.
            </p>

            <ul className="mt-8 space-y-5">
              {HIGHLIGHTS.map(({ icon: Icon, title, body }, i) => (
                <motion.li
                  key={title}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 + i * 0.08 }}
                  className="flex items-start gap-3"
                >
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/12 text-primary ring-1 ring-inset ring-primary/20">
                    <Icon className="h-4 w-4" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-foreground">{title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{body}</p>
                  </div>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          <div className="text-xs text-muted-foreground">
            Trusted by teams shipping precision fulfillment.
          </div>
        </div>
      </div>
    </div>
  );
}
